import React, { useState } from 'react';
import { User, Mail, Phone, Building2, Landmark, Users, CheckCircle, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface B2BFormState {
  nomeGestor: string;
  emailCorporativo: string;
  telefone: string;
  nomeEmpresa: string;
  nif: string;
  estimativaColaboradores: string;
}

const B2BLeadForm: React.FC = () => {
  const { t } = useTranslation();
  const isEng = t('common.loading') === 'Loading...';

  const [formData, setFormData] = useState<B2BFormState>({
    nomeGestor: '',
    emailCorporativo: '',
    telefone: '',
    nomeEmpresa: '',
    nif: '',
    estimativaColaboradores: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [errors, setErrors] = useState<Partial<B2BFormState>>({});

  const validate = (): boolean => {
    const newErrors: Partial<B2BFormState> = {};
    if (!formData.nomeGestor.trim()) newErrors.nomeGestor = isEng ? 'Manager name is required' : 'Nome do gestor é obrigatório';
    if (!formData.emailCorporativo.trim()) {
      newErrors.emailCorporativo = isEng ? 'Corporate email is required' : 'Email corporativo é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(formData.emailCorporativo)) {
      newErrors.emailCorporativo = isEng ? 'Invalid email' : 'Email inválido';
    }
    if (!formData.telefone.trim()) {
      newErrors.telefone = isEng ? 'Phone is required' : 'Telefone é obrigatório';
    } else if (!/^\d{9}$/.test(formData.telefone.replace(/\s/g, ''))) {
      newErrors.telefone = isEng ? 'Phone must contain 9 digits' : 'Telefone deve ter 9 dígitos';
    }
    if (!formData.nomeEmpresa.trim()) newErrors.nomeEmpresa = isEng ? 'Company name is required' : 'Nome da empresa é obrigatório';
    if (!formData.nif.trim()) newErrors.nif = isEng ? 'Tax ID is required' : 'NIF é obrigatório';
    if (!formData.estimativaColaboradores) newErrors.estimativaColaboradores = isEng ? 'Select an estimate' : 'Selecione uma estimativa';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof B2BFormState, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const { formHandler } = await import('../services/formHandler');
      const result = await formHandler.submitForm({
        nome: formData.nomeGestor,
        email: formData.emailCorporativo,
        telefone: formData.telefone,
        nomeGestor: formData.nomeGestor,
        emailCorporativo: formData.emailCorporativo,
        nomeEmpresa: formData.nomeEmpresa,
        nif: formData.nif,
        estimativaColaboradores: formData.estimativaColaboradores
      }, 'b2b');

      if (result.success) {
        setSubmitStatus('success');
        setFormData({
          nomeGestor: '',
          emailCorporativo: '',
          telefone: '',
          nomeEmpresa: '',
          nif: '',
          estimativaColaboradores: ''
        });
      } else {
        setSubmitStatus('error');
        setErrorMessage(result.message);
      }
    } catch (err) {
      setSubmitStatus('error');
      setErrorMessage(isEng ? 'Submission error. Please try again later.' : 'Erro na submissão do formulário. Tente novamente mais tarde.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="b2b-lead-form" className="py-20 bg-gray-50 relative">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden grid md:grid-cols-5">
          {/* Left Side: Call to Action */}
          <div className="md:col-span-2 bg-gradient-to-br from-[#FF6B35] to-[#FF4500] p-8 md:p-12 text-white flex flex-col justify-between">
            <div>
              <span className="bg-white/20 text-white border border-white/30 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider inline-block mb-4">
                B2B DEMO
              </span>
              <h3 className="text-2xl md:text-3xl font-extrabold mb-6 leading-tight">
                {isEng ? "Schedule a Free Demo" : "Agende uma Demonstração Gratuita"}
              </h3>
              <p className="text-white/80 text-sm leading-relaxed mb-6">
                {isEng 
                  ? "Discover how MotoRush can cut delivery and staff commute costs by up to 40% in Luanda." 
                  : "Descubra como a MotoRush pode cortar em até 40% os custos de transporte de mercadorias e deslocamentos dos seus colaboradores em Luanda."}
              </p>
            </div>
            <div className="text-xs text-white/60">
              {isEng 
                ? "* Expert customer support and free setup of your account in less than 24h." 
                : "* Suporte ao cliente especializado e setup gratuito da sua conta corporativa em menos de 24 horas."}
            </div>
          </div>

          {/* Right Side: Form */}
          <div className="md:col-span-3 p-8 md:p-12">
            {submitStatus === 'success' ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-10 animate-fadeIn">
                <CheckCircle className="w-16 h-16 text-green-500 mb-6" />
                <h4 className="text-2xl font-bold text-gray-800 mb-3">
                    {isEng ? "Thank You!" : "Obrigado pelo Contacto!"}
                </h4>
                <p className="text-gray-600 max-w-sm mb-6 text-sm">
                  {isEng 
                    ? "Your demo request has been sent successfully. A manager will contact you within 2 hours." 
                    : "O seu pedido de demonstração foi recebido com sucesso. Um gestor de conta corporativa da MotoRush entrará em contacto nas próximas 2 horas."}
                </p>
                <button
                  onClick={() => setSubmitStatus('idle')}
                  className="bg-[#FF6B35] text-white px-6 py-2.5 rounded-full font-semibold hover:bg-orange-600 transition"
                >
                  {isEng ? "Back to form" : "Voltar ao formulário"}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="border-b border-gray-100 pb-4 mb-2">
                  <h4 className="text-lg font-bold text-gray-800">
                      {isEng ? "Corporate Contact Form" : "Formulário de Contacto Corporativo"}
                  </h4>
                  <p className="text-xs text-gray-500">
                      {isEng ? "Enter company details so we can tailor your SLA." : "Introduza os dados da sua empresa para podermos personalizar o seu SLA."}
                  </p>
                </div>

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl flex items-center gap-3 text-sm animate-shake">
                    <AlertCircle className="w-5 h-5 shrink-0" />
                    <span>{errorMessage}</span>
                  </div>
                )}

                <div className="grid sm:grid-cols-2 gap-4">
                  {/* Nome do Gestor */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
                      <User size={14} className="text-gray-400" />
                      {isEng ? "Manager Name *" : "Nome do Gestor *"}
                    </label>
                    <input
                      type="text"
                      value={formData.nomeGestor}
                      onChange={(e) => handleInputChange('nomeGestor', e.target.value)}
                      placeholder={isEng ? "Ex: John Doe" : "Ex: João Silva"}
                      className={`w-full px-4 py-2.5 border ${errors.nomeGestor ? 'border-red-500 focus:ring-red-100' : 'border-gray-200 focus:ring-orange-100'} rounded-xl text-sm focus:outline-none focus:ring-4 focus:border-[#FF6B35] transition`}
                    />
                    {errors.nomeGestor && <span className="text-red-500 text-[10px] mt-1 block">{errors.nomeGestor}</span>}
                  </div>

                  {/* Email Corporativo */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
                      <Mail size={14} className="text-gray-400" />
                      {isEng ? "Corporate Email *" : "Email Corporativo *"}
                    </label>
                    <input
                      type="email"
                      value={formData.emailCorporativo}
                      onChange={(e) => handleInputChange('emailCorporativo', e.target.value)}
                      placeholder="name@company.com"
                      className={`w-full px-4 py-2.5 border ${errors.emailCorporativo ? 'border-red-500 focus:ring-red-100' : 'border-gray-200 focus:ring-orange-100'} rounded-xl text-sm focus:outline-none focus:ring-4 focus:border-[#FF6B35] transition`}
                    />
                    {errors.emailCorporativo && <span className="text-red-500 text-[10px] mt-1 block">{errors.emailCorporativo}</span>}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  {/* Telefone */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
                      <Phone size={14} className="text-gray-400" />
                      {isEng ? "Contact Phone *" : "Telefone de Contacto *"}
                    </label>
                    <input
                      type="tel"
                      value={formData.telefone}
                      onChange={(e) => handleInputChange('telefone', e.target.value)}
                      placeholder="9XXXXXXXX"
                      className={`w-full px-4 py-2.5 border ${errors.telefone ? 'border-red-500 focus:ring-red-100' : 'border-gray-200 focus:ring-orange-100'} rounded-xl text-sm focus:outline-none focus:ring-4 focus:border-[#FF6B35] transition`}
                    />
                    {errors.telefone && <span className="text-red-500 text-[10px] mt-1 block">{errors.telefone}</span>}
                  </div>

                  {/* Nome da Empresa */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
                      <Building2 size={14} className="text-gray-400" />
                      {isEng ? "Company Name *" : "Nome da Empresa *"}
                    </label>
                    <input
                      type="text"
                      value={formData.nomeEmpresa}
                      onChange={(e) => handleInputChange('nomeEmpresa', e.target.value)}
                      placeholder="Company Ltd."
                      className={`w-full px-4 py-2.5 border ${errors.nomeEmpresa ? 'border-red-500 focus:ring-red-100' : 'border-gray-200 focus:ring-orange-100'} rounded-xl text-sm focus:outline-none focus:ring-4 focus:border-[#FF6B35] transition`}
                    />
                    {errors.nomeEmpresa && <span className="text-red-500 text-[10px] mt-1 block">{errors.nomeEmpresa}</span>}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  {/* NIF */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
                      <Landmark size={14} className="text-gray-400" />
                      {isEng ? "Company Tax ID (NIF) *" : "NIF da Empresa *"}
                    </label>
                    <input
                      type="text"
                      value={formData.nif}
                      onChange={(e) => handleInputChange('nif', e.target.value)}
                      placeholder="NIF"
                      className={`w-full px-4 py-2.5 border ${errors.nif ? 'border-red-500 focus:ring-red-100' : 'border-gray-200 focus:ring-orange-100'} rounded-xl text-sm focus:outline-none focus:ring-4 focus:border-[#FF6B35] transition`}
                    />
                    {errors.nif && <span className="text-red-500 text-[10px] mt-1 block">{errors.nif}</span>}
                  </div>

                  {/* Estimativa Colaboradores */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
                      <Users size={14} className="text-gray-400" />
                      {isEng ? "Estimated Employees *" : "Estimativa de Colaboradores *"}
                    </label>
                    <select
                      value={formData.estimativaColaboradores}
                      onChange={(e) => handleInputChange('estimativaColaboradores', e.target.value)}
                      className={`w-full px-4 py-2.5 bg-white border ${errors.estimativaColaboradores ? 'border-red-500 focus:ring-red-100' : 'border-gray-200 focus:ring-orange-100'} rounded-xl text-sm focus:outline-none focus:ring-4 focus:border-[#FF6B35] transition`}
                    >
                      <option value="">{isEng ? "Select estimate" : "Selecione a quantidade"}</option>
                      <option value="1-10">{isEng ? "1 to 10 employees" : "1 a 10 colaboradores"}</option>
                      <option value="11-50">{isEng ? "11 to 50 employees" : "11 a 50 colaboradores"}</option>
                      <option value="50+">{isEng ? "Over 50 employees" : "Mais de 50 colaboradores"}</option>
                    </select>
                    {errors.estimativaColaboradores && <span className="text-red-500 text-[10px] mt-1 block">{errors.estimativaColaboradores}</span>}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#FF6B35] hover:bg-orange-600 text-white font-bold py-3 px-6 rounded-xl transition-all shadow-lg hover:shadow-orange-200 text-sm disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 transform hover:-translate-y-0.5"
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin" /> {isEng ? "Sending..." : "Enviando pedido..."}
                    </>
                  ) : (isEng ? "Request Quote & Demo" : "Solicitar Demonstração")}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default B2BLeadForm;
